/**
 * UserMenu - Menu de usuário premium com dropdown elegante
 *
 * Substitui o tooltip básico do avatar por um menu estruturado
 * com opções de perfil, preferências, alteração de senha, calendário e logout.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-04 v1.7.0 - Remove contornos e integra cronômetro e avatar por sombra premium.
 *   RELEASE: 2026-10-04 v1.6.0 - Une cronômetro e avatar em um único contorno sem sobreposição.
 *   RELEASE: 2026-10-04 v1.5.0 - Integra a cápsula do cronômetro de tarefas
 *            imediatamente antes do avatar, preservando ações independentes.
 *   RELEASE: 2026-08-08 v1.4.0 - Popover reorganizado no padrão Pipedrive:
 *            seção "Minha conta" + nova seção "Visão geral da empresa"
 *            (Configurações da empresa, Gerenciar usuários, Permissões e
 *            Planos) visível apenas para admins do tenant e respeitando as
 *            permissões existentes. Item "Meu Calendário" removido (já existe
 *            o menu lateral Calendário).
 *   RELEASE: 2026-07-24 v1.3.0 - Seção STATUS grande removida do dropdown. Ao

 *            lado do badge de role há agora um pill compacto "Status" que abre
 *            um Popover secundário com as opções de status + toggle de
 *            distribuição automática. Badge de role reduzido em ~20%.
 *   RELEASE: 2026-07-24 v1.2.0 - Status do agente (online/ausente/ocupado/offline)
 *            migrado do header para dentro do popover. Bolinha indicadora do
 *            status é exibida no canto superior direito do avatar do header e
 *            também sobreposta no avatar grande do popover.
 *   RELEASE: 2026-03-03 v1.1.0 - Adicionada opção "Meu Calendário"
 */


import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Settings, KeyRound, LogOut, ChevronDown, Bell, Loader2, Building2, Users, Shield, CreditCard } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { getStorageUrl } from '@/lib/storage';
import { cn } from '@/lib/utils';
import { ChangePasswordDialog } from './ChangePasswordDialog';

import { NotificationPreferencesSheet } from './notifications/NotificationPreferencesSheet';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { usePermissions } from '@/hooks/usePermissions';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useUserPreferences, useSetDefaultTaskCategory, useSetUserPreferences, type KanbanCategoryView } from '@/hooks/useUserPreferences';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAgentPresence, type OnlineStatus } from '@/hooks/useAgentPresence';
import { TasksQuickHeaderTimer } from '@/components/tasks/quick/TasksQuickHeaderTimer';
import { useTasksQuick } from '@/contexts/TasksQuickContext';


interface UserMenuProps {
  /** Dados do usuário autenticado */
  user: { email?: string } | null;
  /** Perfil do usuário com nome e avatar */
  profile: { full_name: string | null; avatar_url: string | null } | null;
  /** Role atual do usuário no tenant */
  userRole?: 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user';
  /** Callback para abrir modal de perfil */
  onOpenProfile: () => void;
  /** Callback para logout */
  onLogout: () => void;
}

/**
 * Configuração visual dos 4 status de presença. Mesma paleta usada no
 * antigo AgentStatusSelector para manter consistência visual.
 */
const STATUS_CONFIG: Record<OnlineStatus, { label: string; dot: string; description: string }> = {
  online:  { label: 'Online',  dot: 'bg-green-500',  description: 'Disponível para atendimento' },
  away:    { label: 'Ausente', dot: 'bg-yellow-500', description: 'Temporariamente indisponível' },
  busy:    { label: 'Ocupado', dot: 'bg-red-500',    description: 'Não receber novas conversas' },
  offline: { label: 'Offline', dot: 'bg-gray-400',   description: 'Desconectado do sistema' },
};

const STATUS_ORDER: OnlineStatus[] = ['online', 'away', 'busy', 'offline'];

/**
 * Extrai as iniciais do nome ou email do usuário
 */
const getInitials = (name: string): string => {
  if (!name) return 'U';
  if (name.includes('@')) {
    return name.split('@')[0].charAt(0).toUpperCase();
  }
  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getRoleLabel = (role?: string): string => {
  const roleLabels: Record<string, string> = {
    root_admin: 'Super Admin',
    tenant_admin: 'Administrador',
    tenant_supervisor: 'Supervisor',
    agent: 'Agente',
    user: 'Usuário',
  };
  return roleLabels[role || 'user'] || 'Usuário';
};

const getRoleBadgeVariant = (role?: string): 'default' | 'secondary' | 'outline' => {
  if (role === 'root_admin' || role === 'tenant_admin') return 'default';
  if (role === 'tenant_supervisor') return 'secondary';
  return 'outline';
};

export function UserMenu({ user, profile, userRole, onOpenProfile, onLogout }: UserMenuProps) {
  const { t } = useTranslation('sidebar');
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [notificationsPrefsOpen, setNotificationsPrefsOpen] = useState(false);
  // Permissões: controlam a seção "Visão geral da empresa" (admin do tenant).
  const { isAdmin, canView } = usePermissions();

  // Atalhos administrativos exibidos no popover. Cada item respeita a
  // menuKey já usada no navRegistry; sem permissão, o item não é renderizado.
  const companyItems = [
    { url: '/tenant/company', menuKey: 'company', icon: Building2, title: 'Configurações da empresa', description: 'Dados e parâmetros' },
    { url: '/tenant/agents', menuKey: 'agents', icon: Users, title: 'Gerenciar usuários', description: 'Agentes e acessos' },
    { url: '/tenant/permissions', menuKey: 'settings', icon: Shield, title: 'Permissões', description: 'Perfis e permissões' },
    { url: '/tenant/plans', menuKey: 'company', icon: CreditCard, title: 'Planos', description: 'Assinatura e add-ons' },
  ].filter((item) => canView(item.menuKey));

  // Estado de presença do agente (migrado do header p/ dentro do popover).
  const {
    onlineStatus,
    availableForDistribution,
    updating,
    updateStatus,
    setAvailable,
  } = useAgentPresence();

  const currentStatus = STATUS_CONFIG[onlineStatus];
  const quickTasks = useTasksQuick();

  return (
    <>
      <div className={cn(
        'flex items-center',
        quickTasks?.visible && 'overflow-hidden rounded-full bg-background shadow-lg transition-shadow duration-200 hover:shadow-xl',
      )}>
        <TasksQuickHeaderTimer />
        <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Avatar clicável com indicador de dropdown */}
          <button className={cn(
            'flex h-9 items-center gap-1 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            quickTasks?.visible && 'rounded-l-none bg-primary/5 pl-1',
          )}>
            <div className="relative">
              <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all">
                <AvatarImage
                  src={getStorageUrl(profile?.avatar_url)}
                  alt={profile?.full_name || 'Avatar'}
                />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {getInitials(profile?.full_name || user?.email || '')}
                </AvatarFallback>
              </Avatar>
              {/* Bolinha indicadora do status no canto superior direito */}
              <span
                aria-label={`Status: ${currentStatus.label}`}
                className={cn(
                  'absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card',
                  currentStatus.dot,
                )}
              />
            </div>
            <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-72 p-0 rounded-xl shadow-lg border bg-popover z-50"
        >
          {/* Header com informações do usuário */}
          <div className="p-4 bg-muted/30 border-b">
            <div className="flex items-start gap-3">
              {/* Avatar maior no header do popover (com mesma bolinha de status) */}
              <div className="relative">
                <Avatar className="h-12 w-12 ring-2 ring-background shadow-sm">
                  <AvatarImage
                    src={getStorageUrl(profile?.avatar_url)}
                    alt={profile?.full_name || 'Avatar'}
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                    {getInitials(profile?.full_name || user?.email || '')}
                  </AvatarFallback>
                </Avatar>
                <span
                  aria-hidden
                  className={cn(
                    'absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-background',
                    currentStatus.dot,
                  )}
                />
              </div>

              <div className="flex-1 min-w-0">
                {profile?.full_name && (
                  <p className="font-semibold text-foreground truncate">
                    {profile.full_name}
                  </p>
                )}
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email}
                </p>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <Badge
                    variant={getRoleBadgeVariant(userRole)}
                    className="text-[10px] px-1.5 py-0 h-4 leading-none"
                  >
                    {getRoleLabel(userRole)}
                  </Badge>

                  {/* Pill "Status" — abre popover secundário com opções */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 h-4 px-1.5 rounded-full border border-border bg-background hover:bg-accent transition-colors text-[10px] font-medium leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Alterar status (atual: ${currentStatus.label})`}
                      >
                        <span className={cn('h-1.5 w-1.5 rounded-full', currentStatus.dot)} />
                        <span>{currentStatus.label}</span>
                        <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={6}
                      className="w-64 p-2"
                      // Impede que o click dentro do popover propague e feche o DropdownMenu pai
                      onClick={(e) => e.stopPropagation()}
                    >
                      {STATUS_ORDER.map((status) => {
                        const cfg = STATUS_CONFIG[status];
                        const isSelected = status === onlineStatus;
                        return (
                          <button
                            key={status}
                            type="button"
                            disabled={updating}
                            onClick={() => {
                              if (!updating && status !== onlineStatus) {
                                void updateStatus(status);
                              }
                            }}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-accent transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
                              isSelected && 'bg-accent',
                            )}
                          >
                            <span className="relative flex h-4 w-4 items-center justify-center">
                              {updating && isSelected ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                              ) : (
                                <span className={cn('h-2.5 w-2.5 rounded-full', cfg.dot)} />
                              )}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{cfg.label}</p>
                              <p className="text-xs text-muted-foreground">{cfg.description}</p>
                            </div>
                          </button>
                        );
                      })}

                      {/* Toggle de disponibilidade para distribuição automática */}
                      <div className="mt-1 px-3 py-2 rounded-lg bg-muted/40">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="available-distribution"
                            checked={availableForDistribution}
                            onCheckedChange={(checked) => {
                              void setAvailable(Boolean(checked));
                            }}
                            disabled={updating || onlineStatus === 'offline'}
                          />
                          <Label
                            htmlFor="available-distribution"
                            className="text-sm cursor-pointer font-medium"
                          >
                            Receber distribuição automática
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          Quando ativo, você recebe conversas automaticamente
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>



          {/* Label da seção */}
          <DropdownMenuLabel className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Minha Conta
          </DropdownMenuLabel>

          {/* Opções do menu */}
          {/* v1.5.0: itens compactos — somente título + ícone (sem descrições) */}
          <div className="px-2 pb-1.5">
            {/* Perfil */}
            <DropdownMenuItem
              onClick={onOpenProfile}
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
            >
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Perfil</span>
            </DropdownMenuItem>

            {/* Preferências pessoais */}
            <DropdownMenuItem
              onClick={() => setPreferencesOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
            >
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Preferências pessoais</span>
            </DropdownMenuItem>

            {/* Preferências de Notificação */}
            <DropdownMenuItem
              onClick={() => setNotificationsPrefsOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
            >
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Preferências de Notificação</span>
            </DropdownMenuItem>

            {/* Alterar Senha */}
            <DropdownMenuItem
              onClick={() => setChangePasswordOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
            >
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Alterar Senha</span>
            </DropdownMenuItem>
          </div>

          {/* ================================================================
              Seção "Visão geral da empresa" — apenas admins do tenant.
              URLs e menuKeys reaproveitados do navRegistry (categoria settings).
              ================================================================ */}
          {isAdmin && companyItems.length > 0 && (
            <>
              <DropdownMenuSeparator className="my-0" />
              <DropdownMenuLabel className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Visão geral da empresa
              </DropdownMenuLabel>
              <div className="px-2 pb-1.5">
                {companyItems.map((item) => (
                  <DropdownMenuItem
                    key={item.url}
                    onClick={() => navigate(item.url)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                  >
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{item.title}</span>
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}

          <DropdownMenuSeparator className="my-0" />


          {/* Logout */}
          <div className="p-2">
            <DropdownMenuItem
              onClick={onLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm">{t('tooltips.logout', 'Sair')}</span>
            </DropdownMenuItem>
          </div>

        </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Modal de alteração de senha */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />

      {/* Sheet de Preferências de Notificação */}
      <NotificationPreferencesSheet
        open={notificationsPrefsOpen}
        onOpenChange={setNotificationsPrefsOpen}
      />

      {/* Dialog de Preferências */}
      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preferências</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Idioma</label>
              <LanguageSwitcher variant="default" />
            </div>

            <TaskDefaultCategoryField />

            <KanbanViewFormatField
              label="Formato do Kanban de Conversas"
              description="Escolha como as categorias aparecem no topo do Kanban de Conversas."
              prefKey="kanban_view_conversations"
            />

            <KanbanViewFormatField
              label="Formato do Kanban de Tarefas"
              description="Escolha como as categorias aparecem no topo do Kanban de Tarefas."
              prefKey="kanban_view_tasks"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Campo de seleção da categoria padrão do Kanban de Tarefas.
 */
function TaskDefaultCategoryField() {
  const { categories, isLoading: loadingCategories } = useTaskCategories(true);
  const { data: prefs, isLoading: loadingPrefs } = useUserPreferences();
  const setDefault = useSetDefaultTaskCategory();

  const NONE = '__none__';
  const currentValue = prefs?.default_task_category_id ?? NONE;

  const handleChange = (next: string) => {
    setDefault.mutate(next === NONE ? null : next);
  };

  return (
    <div>
      <label className="text-sm font-medium text-foreground mb-2 block">
        Categoria padrão do Kanban de Tarefas
      </label>
      <Select
        value={currentValue}
        onValueChange={handleChange}
        disabled={loadingCategories || loadingPrefs || setDefault.isPending}
      >
        <SelectTrigger>
          <SelectValue placeholder="Selecione uma categoria" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— Primeira disponível —</SelectItem>
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={cat.id}>
              {cat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground mt-1.5">
        Define qual aba abre primeiro ao acessar o Kanban de Tarefas.
      </p>
    </div>
  );
}

/**
 * Campo de escolha do formato de visualização de categorias em um Kanban.
 */
function KanbanViewFormatField({
  label,
  description,
  prefKey,
}: {
  label: string;
  description: string;
  prefKey: 'kanban_view_conversations' | 'kanban_view_tasks';
}) {
  const { data: prefs, isLoading: loadingPrefs } = useUserPreferences();
  const setPrefs = useSetUserPreferences();

  const current: KanbanCategoryView = prefs?.[prefKey] ?? 'tabs';

  const handleChange = (next: string) => {
    if (next !== 'tabs' && next !== 'select') return;
    setPrefs.mutate({ [prefKey]: next } as any);
  };

  return (
    <div>
      <label className="text-sm font-medium text-foreground mb-2 block">{label}</label>
      <RadioGroup
        value={current}
        onValueChange={handleChange}
        className="flex gap-4"
        disabled={loadingPrefs || setPrefs.isPending}
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="tabs" id={`${prefKey}-tabs`} />
          <span className="text-sm">Abas</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <RadioGroupItem value="select" id={`${prefKey}-select`} />
          <span className="text-sm">Seleção</span>
        </label>
      </RadioGroup>
      <p className="text-xs text-muted-foreground mt-1.5">{description}</p>
    </div>
  );
}
